<template>
  <div
    class="v3-input-field"
    :class="{
      'is-invalid': isInvalid || invalidFeedback,
      'with-prepend': prependIcon,
      'with-append': appendIcon || appendText
    }"
  >
    <div
      class="v3-input-wrap"
      :class="{ 'v3-input-wrap--append-button-flex': appendButton }"
    >
      <div v-if="prependIcon" class="prepended-icon">
        <i :class="prependIcon"></i>
      </div>
      <textarea
        :rows="rows"
        v-if="type == 'textarea'"
        ref="fieldElementRef"
        v-bind="{ id }"
        :maxlength="effectiveMaxLength"
        :disabled="disabled"
        :readonly="readonly"
        @blur="onBlur"
        :value="inputValue"
        @input="onModelUpdate"
        :placeholder="placeholder"
        type="text"
        :autocomplete="autocompleteAttr"
        :aria-invalid="isFieldInvalid ? 'true' : undefined"
        :aria-required="ariaRequired ? 'true' : undefined"
        :aria-describedby="invalidDescribedBy"
        :aria-labelledby="validAriaLabelledBy || undefined"
        :aria-label="resolvedInputAriaLabel"
        :aria-expanded="normalizedInputAriaExpanded"
        :aria-haspopup="inputAriaHaspopup || undefined"
      ></textarea>
      <input
        v-else
        v-bind="{ id }"
        ref="fieldElementRef"
        :autocomplete="autocompleteAttr"
        :v-noautofillinput="vNoAutoFillInput"
        :disabled="disabled"
        :readonly="readonly"
        @blur="onBlur"
        :value="inputValue"
        @input="onModelUpdate"
        :placeholder="placeholder"
        :type="inputType"
        :minlength="minLength"
        :maxlength="maxLength"
        :min="minNumber"
        :max="maxNumber"
        :data-test-id="testId"
        :aria-invalid="isFieldInvalid ? 'true' : undefined"
        :aria-required="ariaRequired ? 'true' : undefined"
        :aria-describedby="invalidDescribedBy"
        :aria-labelledby="validAriaLabelledBy || undefined"
        :aria-label="resolvedInputAriaLabel"
        :aria-expanded="normalizedInputAriaExpanded"
        :aria-haspopup="inputAriaHaspopup || undefined"
        @keydown="onInputActionKeydown"
      />
      <div
        v-if="type == 'textarea' && effectiveMaxLength"
        class="text-end mt-1"
      >
        <template v-if="showCharCounter">
          {{ currentCharCount }}/{{ effectiveMaxLength }}
        </template>
        <template v-else>
          {{ $t("common.characters_max", { count: effectiveMaxLength }) }}
        </template>
      </div>
      <div
        v-if="appendIcon || appendText"
        class="prepended-icon append"
        style="-webkit-text-security: none"
      >
        <button
          v-if="appendIcon"
          type="button"
          class="append-icon-button"
          :aria-label="appendIconAriaLabel"
          @click="onClickAppendIcon()"
        >
          <i :class="appendIcon" aria-hidden="true"></i>
        </button>
        <span
          class="appended-text"
          v-if="appendText"
          :id="appendTextElementId || undefined"
          >{{ appendText }}</span
        >
      </div>
      <button
        v-if="appendButton"
        type="button"
        @click="onClickAppendButton()"
        class="appended-button"
        :aria-label="appendButtonAriaLabel"
        style="-webkit-text-security: none"
      >
        <i
          :class="appendButton.icon"
          class="appended-button-icon"
          aria-hidden="true"
        ></i>
        <span class="appended-button-text ms-1">{{ appendButton.text }}</span>
      </button>
    </div>
    <div
      v-if="invalidFeedbackMessage"
      :id="invalidFeedbackId"
      ref="invalidMessageEl"
      class="invalid-message text-start"
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
    >
      {{ invalidFeedbackMessage }}
    </div>
  </div>
</template>
<script>
import { computed, onMounted, ref, watch, nextTick } from "vue"
import { useI18n } from "vue-i18n"
export default {
  props: {
    id: String,
    placeholder: String,
    modelValue: [String, Array, Number, Object],
    isInvalid: Boolean,
    invalidFeedback: [String, Array],
    prependIcon: String,
    appendIcon: String,
    appendIconLabel: {
      type: String,
      default: ""
    },
    innerLabel: String,
    disabled: Boolean,
    vNoAutoFillInput: Boolean,
    /** HTML autocomplete: string token(s) or `false` for `"off"`. */
    autocomplete: [String, Boolean],
    /** Id of the associated `<label>` (for `aria-labelledby` with append units). */
    labelId: {
      type: String,
      default: ""
    },
    inputAriaLabel: {
      type: String,
      default: ""
    },
    inputAriaExpanded: {
      type: [String, Boolean],
      default: undefined
    },
    inputAriaHaspopup: {
      type: String,
      default: ""
    },
    type: {
      type: String,
      default: "input"
    },
    readonly: {
      type: Boolean,
      default: false
    },
    rows: {
      type: [String, Number],
      default: 1
    },
    appendText: {
      type: String,
      default: ""
    },
    appendButton: {
      type: Object,
      default: null
    },
    appendButtonLabel: {
      type: String,
      default: ""
    },
    maxChars: {
      type: String,
      default: null
    },
    inputType: {
      type: String,
      default: "text"
    },
    minLength: {
      type: String,
      default: null
    },
    maxLength: {
      type: String,
      default: null
    },
    minNumber: {
      type: String,
      default: null
    },
    maxNumber: {
      type: String,
      default: null
    },
    isFocused: {
      type: Boolean,
      default: false
    },
    testId: {
      type: String,
      default: ""
    },
    ariaLabel: {
      type: String,
      default: ""
    },
    /** WCAG 1.4.1: pair with visible "required" text on the label, not color alone */
    ariaRequired: {
      type: Boolean,
      default: false
    },
    /** When true, the `maxChars` counter renders as `current/max` (e.g. `12/25`). */
    showCharCounter: {
      type: Boolean,
      default: false
    }
  },
  emits: ["update:modelValue", "blur", "clickAppendIcon", "clickAppendButton"],
  setup(props, { emit }) {
    const { t } = useI18n()
    const fieldElementRef = ref(null)
    const invalidMessageEl = ref(null)

    const onModelUpdate = (value) => {
      emit("update:modelValue", value.target.value)
    }
    const onBlur = (event) => {
      emit("blur", event)
    }
    const invalidFeedbackMessage = computed(() => {
      return props.invalidFeedback && Array.isArray(props.invalidFeedback)
        ? props.invalidFeedback[0]
        : props.invalidFeedback
    })
    const isFieldInvalid = computed(
      () => !!(props.isInvalid || invalidFeedbackMessage.value)
    )
    const invalidFeedbackId = computed(() =>
      props.id ? `${props.id}-invalid` : undefined
    )
    const invalidDescribedBy = computed(() =>
      invalidFeedbackMessage.value && invalidFeedbackId.value
        ? invalidFeedbackId.value
        : undefined
    )
    const appendTextElementId = computed(() =>
      props.id && props.appendText ? `${props.id}-append-unit` : null
    )

    const hasAccessibleText = (el) => {
      if (!el) return false
      const acc =
        (el.textContent && el.textContent.trim()) ||
        el.getAttribute("aria-label") ||
        el.getAttribute("alt")
      return Boolean(acc)
    }

    const validAriaLabelledBy = ref("")
    const hasNativeLabel = ref(false)

    const labelForIdSelector = (id) => {
      if (id == null) return 'label[for=""]'
      if (typeof CSS !== "undefined" && CSS.escape) {
        return `label[for="${CSS.escape(String(id))}"]`
      }
      return `label[for="${String(id).replace(/"/g, '\\"')}"]`
    }

    const syncFormInputA11y = () => {
      if (typeof document === "undefined" || !document.getElementById) {
        return
      }

      const mainIds = []
      if (props.labelId?.trim()) {
        for (const part of props.labelId.split(/\s+/)) {
          const tid = part.trim()
          if (!tid) continue
          const el = document.getElementById(tid)
          if (hasAccessibleText(el)) {
            mainIds.push(tid)
          }
        }
      }

      let by = ""
      if (mainIds.length) {
        by = mainIds.join(" ")
        if (props.appendText && appendTextElementId.value) {
          const a = document.getElementById(appendTextElementId.value)
          if (hasAccessibleText(a)) {
            by = `${by} ${appendTextElementId.value}`.trim()
          }
        }
      }
      validAriaLabelledBy.value = by

      let foundNative = false
      if (props.id) {
        const lab = document.querySelector(labelForIdSelector(props.id))
        if (hasAccessibleText(lab)) {
          foundNative = true
        }
      }
      if (!foundNative && fieldElementRef.value) {
        const w = fieldElementRef.value.closest?.("label")
        if (w && w.textContent?.trim()) {
          foundNative = true
        }
      }
      hasNativeLabel.value = foundNative
    }

    /** 4.1.2: valid `aria-labelledby` / native label / explicit name / placeholder / i18n fallback (single path). */
    const resolvedInputAriaLabel = computed(() => {
      if (validAriaLabelledBy.value) {
        return undefined
      }
      if (props.ariaLabel?.trim()) {
        return props.ariaLabel.trim()
      }
      if (props.inputAriaLabel?.trim()) {
        return props.inputAriaLabel.trim()
      }
      if (hasNativeLabel.value) {
        return undefined
      }
      if (props.placeholder?.trim()) {
        return props.placeholder.trim()
      }
      if (props.innerLabel?.trim()) {
        return props.innerLabel.trim()
      }
      return props.type === "textarea"
        ? t("common.a11y_textarea_default_accessible_name")
        : t("common.a11y_input_default_accessible_name")
    })
    const autocompleteAttr = computed(() => {
      if (typeof props.autocomplete === "string") return props.autocomplete
      if (props.autocomplete === false) return "off"
      return undefined
    })
    const normalizedInputAriaExpanded = computed(() => {
      if (typeof props.inputAriaExpanded === "boolean") {
        return props.inputAriaExpanded ? "true" : "false"
      }
      return props.inputAriaExpanded || undefined
    })
    const appendIconAriaLabel = computed(() => {
      if (props.appendIconLabel?.trim()) return props.appendIconLabel
      if (props.appendIcon?.includes("calendar")) return "Open calendar"
      return "Open field action"
    })
    const appendButtonAriaLabel = computed(() => {
      if (props.appendButtonLabel?.trim()) return props.appendButtonLabel
      if (props.appendButton?.text?.trim()) return props.appendButton.text
      return "Field action"
    })
    const inputValue = computed(() => {
      return props.innerLabel
        ? props.innerLabel + " " + props.modelValue
        : props.modelValue
    })

    const currentCharCount = computed(() => {
      const v = props.modelValue
      if (v == null) return 0
      return String(v).length
    })

    /** Textareas use `maxChars`; inputs use `maxLength` — accept either for limits. */
    const effectiveMaxLength = computed(
      () => props.maxChars || props.maxLength || null
    )

    const onClickAppendIcon = () => {
      emit("clickAppendIcon")
    }

    const onClickAppendButton = () => {
      emit("clickAppendButton")
    }

    const onInputActionKeydown = (event) => {
      if (props.disabled) {
        return
      }

      if (!props.appendButton && !props.appendIcon) {
        return
      }

      const isEnterKey = event.key === "Enter"
      const isSpaceKey =
        event.key === " " || event.key === "Space" || event.key === "Spacebar"
      if (!isEnterKey && !isSpaceKey) {
        return
      }

      event.preventDefault()
      if (props.appendButton) {
        emit("clickAppendButton")
        return
      }
      emit("clickAppendIcon")
    }

    watch(
      () => [
        props.labelId,
        props.id,
        props.appendText,
        appendTextElementId.value
      ],
      () => {
        nextTick(() => {
          syncFormInputA11y()
        })
      },
      { flush: "post" }
    )

    onMounted(() => {
      nextTick(() => {
        syncFormInputA11y()
        if (props.isFocused && fieldElementRef.value) {
          fieldElementRef.value.focus()
        }
      })
    })

    watch(
      invalidFeedbackMessage,
      async (msg) => {
        if (!msg) return
        await nextTick()
        const el = invalidMessageEl.value
        if (!el) return
        el.scrollIntoView({ block: "nearest", behavior: "smooth" })
      },
      { flush: "post" }
    )

    return {
      onModelUpdate,
      onBlur,
      invalidFeedbackMessage,
      isFieldInvalid,
      invalidFeedbackId,
      invalidDescribedBy,
      appendTextElementId,
      validAriaLabelledBy,
      resolvedInputAriaLabel,
      autocompleteAttr,
      normalizedInputAriaExpanded,
      appendIconAriaLabel,
      appendButtonAriaLabel,
      inputValue,
      currentCharCount,
      effectiveMaxLength,
      onClickAppendIcon,
      onClickAppendButton,
      onInputActionKeydown,
      fieldElementRef,
      invalidMessageEl,
      t
    }
  }
}
</script>
